import { useState } from 'react';
import jsPDF from 'jspdf';

// Bar fill colors matching _gantt-chart.scss $color variables
const BAR_FILLS = {
  'bar--high': '#d2555a',
  'bar--medium': '#f7a021',
  'bar--low': '#6dc4a3',
  'bar--default': '#7f8fa4',
  'bar--parent-portfolio': '#153142',
  'bar--portfolio': '#115973',
  'bar--communication': '#6dc4a3',
  'bar--readiness': '#24afdf',
  'bar--training': '#153142',
  'bar--sustainment': '#f7a021',
};

// Explicitly set fill colors for gantt elements after getComputedStyle,
// overriding anything that might have been set incorrectly.
function applyGanttColors(svg) {
  svg.querySelectorAll('.bar').forEach((el) => {
    let fill = '#7f8fa4'; // $blue-grey default
    for (const cls of el.classList) {
      if (BAR_FILLS[cls]) { fill = BAR_FILLS[cls]; break; }
    }
    el.setAttribute('fill', fill);
  });

  svg.querySelectorAll('.chart-label').forEach((el) => {
    el.setAttribute('fill', '#ffffff');
  });

  svg.querySelectorAll('.mile').forEach((el) => {
    el.setAttribute('fill', el.classList.contains('portfolio') ? '#5771ca' : '#27b0e0');
  });

  // Axis lines and domain path: d3 uses stroke/fill="currentColor" which
  // resolves to black in a standalone SVG without CSS. Hardcode from SCSS:
  // .axis { color: $silver (#dfe2e5) }
  svg.querySelectorAll('.axis path, .axis line').forEach((el) => {
    el.setAttribute('stroke', '#dfe2e5'); // $silver
    el.setAttribute('fill', 'none');
  });

  // Axis tick text: .axis .tick text { color: $blue-grey (#7f8fa4) }
  // Current-period labels: .current-month/.current-quarter/etc { color: $dark-blue-grey (#153142) }
  const CURRENT_CLASSES = ['current-day', 'current-month', 'current-quarter', 'current-year'];
  svg.querySelectorAll('.axis text').forEach((el) => {
    const isCurrent = CURRENT_CLASSES.some((cls) => el.classList.contains(cls));
    el.setAttribute('fill', isCurrent ? '#153142' : '#7f8fa4');
  });
}

export const useDownloadPDF = (chartContainerId, scaleType) => {
  const [loading, setLoading] = useState(false);

  const downloadPDF = async () => {
    const container = document.getElementById(chartContainerId);
    if (!container) return;
    setLoading(true);

    try {
      const svgs = Array.from(container.querySelectorAll('svg'));
      if (!svgs.length) return;

      const mainSvg =
        container.querySelector('#ganttChartExport svg') ||
        container.querySelector('#ganttChartPdf svg') ||
        svgs[svgs.length - 1];

      const mainG = mainSvg?.querySelector('g');
      const fullWidth = mainG
        ? Math.ceil(mainG.getBBox().x + mainG.getBBox().width)
        : Math.max(
            ...svgs.map((svg) => parseInt(svg.getAttribute('width') || 0))
          );

      const totalHeight = svgs.reduce(
        (sum, svg) => sum + parseInt(svg.getAttribute('height') || 0),
        0
      );

      const SCALE = 2;
      const canvas = document.createElement('canvas');
      canvas.width = fullWidth * SCALE;
      canvas.height = totalHeight * SCALE;
      const ctx = canvas.getContext('2d');
      ctx.scale(SCALE, SCALE);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, fullWidth, totalHeight);

      let offsetY = 0;
      for (const svg of svgs) {
        const h = parseInt(svg.getAttribute('height') || 0);
        const w = (() => {
          const g = svg.querySelector('g');
          if (!g) return parseInt(svg.getAttribute('width') || 0);
          const bb = g.getBBox();
          return Math.ceil(bb.x + bb.width);
        })();

        svg.setAttribute('width', fullWidth);
        svg.setAttribute('overflow', 'visible');
        svg.removeAttribute('viewBox');
        svg.querySelectorAll('clipPath rect').forEach((r) => {
          r.setAttribute('width', fullWidth);
        });

        svg.querySelectorAll('*').forEach((el) => {
          const cs = window.getComputedStyle(el);
          [
            'fill',
            'stroke',
            'stroke-width',
            'font-size',
            'font-family',
            'opacity',
          ].forEach((prop) => {
            const val = cs.getPropertyValue(prop);
            if (val) el.setAttribute(prop, val);
          });
        });

        // Override with correct gantt colors — getComputedStyle can return
        // wrong values for CSS-class-driven SVG fills in some browsers.
        applyGanttColors(svg);

        const blob = new Blob([new XMLSerializer().serializeToString(svg)], {
          type: 'image/svg+xml',
        });
        const url = URL.createObjectURL(blob);
        const img = await new Promise((res, rej) => {
          const i = new Image();
          i.onload = () => {
            URL.revokeObjectURL(url);
            res(i);
          };
          i.onerror = rej;
          i.src = url;
        });

        ctx.drawImage(img, 0, offsetY, fullWidth, h);
        offsetY += h;
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: fullWidth > totalHeight ? 'landscape' : 'portrait',
        unit: 'px',
        format: [fullWidth, totalHeight],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, fullWidth, totalHeight);
      pdf.save(`gantt-chart-${scaleType}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return { downloadPDF, loading };
};
